import { FormField, Modal, Paragraph, TextInput } from 'canoe';
import { Field, FieldAttributes, Form, Formik } from 'formik';
import {
  ActionFetch,
  ENDPOINTS,
  fetchResource,
  handleErrorResponse,
  useCreatePayload,
} from 'hammock';
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import * as Yup from 'yup';

import { ContentMetadata } from '@candu-ai/content/types';

const validationSchema = Yup.object().shape({
  slug: Yup.string()
    .required('You must specify a slug name')
    .min(3, 'Must have at least 3 characters')
    .matches(/^[a-z0-9_-]+$/, 'Must contain only letters, numbers, dashes or underscores'),
});

interface ChangeSlugProps {
  contentMetadata: ContentMetadata;
  onContentUpdated?: VoidFunction;
  onContentUpdateError?: (error: unknown, variables: ActionFetch) => void;
  onClose: VoidFunction;
}

export const ChangeSlug = ({
  contentMetadata,
  onContentUpdated,
  onContentUpdateError,
  onClose,
}: ChangeSlugProps) => {
  const { update } = useCreatePayload(ENDPOINTS.contentMetadata);

  const [slugName, setSlugName] = useState<string>(contentMetadata.slug);

  const { mutateAsync: updateContent, isLoading } = useMutation(
    () => {
      const payload = update({
        id: contentMetadata.id,
        body: { ...contentMetadata, slug: slugName },
      });
      return fetchResource(payload).catch((e) => handleErrorResponse(e, payload));
    },
    {
      onSuccess: onContentUpdated,
      onError: (e) =>
        onContentUpdateError?.(
          e,
          update({ id: contentMetadata.id, body: { ...contentMetadata, slug: slugName } }),
        ),
    },
  );

  return (
    <>
      <Formik
        onSubmit={(values) => {
          setSlugName(values.slug.trim());
          updateContent();
        }}
        initialValues={{ slug: contentMetadata.slug }}
        validationSchema={validationSchema}
      >
        {({ values, submitForm, errors }) => (
          <Modal severity="Danger" onClose={onClose}>
            <Modal.Header title="Change Slug" />
            <Modal.Body>
              <Paragraph minWidth="none">
                Careful! The content slug is used to identify your content in code. If you change a
                content slug without updating it customers may not be able to access its content.
              </Paragraph>
              <Form>
                <Field name="slug">
                  {({ field, meta }: FieldAttributes<any>) => (
                    <FormField
                      label="New slug name"
                      m="xxs"
                      state={meta.error ? 'danger' : 'none'}
                      feedback={meta.error ? meta.error : ''}
                    >
                      <>
                        <TextInput {...field} state={meta.error ? 'danger' : 'none'} />
                      </>
                    </FormField>
                  )}
                </Field>
              </Form>
            </Modal.Body>
            <Modal.Footer
              primaryAction={{
                disabled: contentMetadata.slug === values.slug || !!errors?.slug?.length,
                variant: 'danger',
                children: 'Change slug',
                loading: isLoading,
                onClick: submitForm,
              }}
            />
          </Modal>
        )}
      </Formik>
    </>
  );
};
